import { call, CallEffect, ForkEffect, select, SelectEffect, takeEvery } from 'redux-saga/effects';
import 'regenerator-runtime/runtime';
import { App, Produktlinie } from '@ps20-frontend/frontend-models';
import {
  TaasRequestModel,
  TaasResponseModel,
  TaasTarifrisikoschluesselURI,
  TaasUriData,
} from '@ps20/bff-models';
import {
  getSelectedProduktlinie,
  ShopTarifierer,
  ShopTarifierungsErgebnisHandler,
} from '@ps20-frontend/preis-lib';
import { safeSaga } from '@ps20-frontend/safe-saga';
import {
  DoRequestResponse,
  GlobalActions,
  GlobalPut,
  ShopTarifierungRequestEvents,
  TarifiereAngebotForShopAction,
} from '@ps20-frontend/shop-integration-redux';
import { isOlderThan } from '@ps20-frontend/utils';
import { selectUnfallVpMaxAlterErsatzgeschaeft } from '@ps20-frontend/selector';
import { IAppState } from '../appState';
import { buildTaasUnfDataForShop } from './transformer/unfData.transformer';

const globalPut = GlobalPut.getInstance();
const globalActions = new GlobalActions();

export const shopTarifierungsErgebnisHandler = new ShopTarifierungsErgebnisHandler<
  IAppState,
  TaasUriData
>(App.UNF, getVereinbarungInfo);
export const shopTarifierer = new ShopTarifierer({
  appId: App.UNF,
  taasDataBuilder: buildTaasUnfDataForShop,
  resultHandler: shopTarifierungsErgebnisHandler,
});

export function* shopTarifierungUnf(): Generator<ForkEffect> {
  yield takeEvery(
    ShopTarifierungRequestEvents.PREISE_SHOP_REQUESTED_SUCCESSFUL_UNF,
    safeSaga(callSuccessOnMatchingAngebotsId, App.UNF),
  );
  yield takeEvery(
    GlobalActions.TARIFIERE_ANGEBOT_FOR_SHOP,
    safeSaga(callTarifierungOnMatchingAngebotsId, App.UNF),
  );
}

export function* callTarifierungOnMatchingAngebotsId(
  tarifiereAngebotAction: TarifiereAngebotForShopAction,
): Generator<SelectEffect | CallEffect> {
  const appState: IAppState = (yield select()) as IAppState;
  const angebotsIdIsMatching = appState.angebotsId === tarifiereAngebotAction.payload.angebotsId;

  if (angebotsIdIsMatching) {
    if (isUnfallTarifierungConditionsOk(appState)) {
      yield call(shopTarifierer.callTarifierung, tarifiereAngebotAction);
    } else {
      yield call([globalPut, globalPut.put], globalActions.tarifierungHasCancelled());
      yield call(
        [globalPut, globalPut.put],
        globalActions.preiseResetDashboard({
          appId: App.UNF,
          angebotsId: appState.angebotsId,
          bruttopreis: 0,
          bruttopreisOhneKombiRabatt: 0,
        }),
      );
    }
  }
}

export function* callSuccessOnMatchingAngebotsId(
  successAction: DoRequestResponse<TaasResponseModel, TaasRequestModel<TaasUriData>>,
): Generator<SelectEffect | CallEffect> {
  const appState: IAppState = (yield select()) as IAppState;
  const originEvent: TarifiereAngebotForShopAction =
    successAction.doRequestAction.payload.originEvent;
  const angebotsIdIsMatching = appState.angebotsId === originEvent.payload.angebotsId;

  if (angebotsIdIsMatching && isUnfallTarifierungConditionsOk(appState)) {
    yield call(shopTarifierer.handleSuccess, successAction);
  }
}

export function getVereinbarungInfo(state: IAppState): string {
  return `${state.vp.vorname} ${state.vp.nachname}, geb. ${state.vp.geburtsdatum}`;
}

export function isUnfallTarifierungConditionsOk(state: IAppState): boolean {
  const unfallVpMaxAlterErsatzgeschaeft = selectUnfallVpMaxAlterErsatzgeschaeft(state);
  const age = unfallVpMaxAlterErsatzgeschaeft || 99;

  const olderThan16 = isOlderThan(state.vp.geburtsdatum, state.vertragsoptionen.beginnDatum, 16);
  const olderThanMax = isOlderThan(state.vp.geburtsdatum, state.vertragsoptionen.beginnDatum, age);
  const selectedProduktlinie: Produktlinie = getSelectedProduktlinie(state.produkt);
  const hasKISselected = selectedProduktlinie?.zusatzoptionen.some(
    z => z.name === TaasTarifrisikoschluesselURI.KINDERINDIVIDUALITAETSSCHUTZ && z.isSelected,
  );
  return state.vp.berufsrisikogruppe !== null && !(olderThan16 && hasKISselected) && !olderThanMax;
}
